import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { ProductImage } from '../../utils/types';
import { uploadProductImage, uploadMultipleProductImages } from '../../services/productImageService';

interface MultipleImageUploadProps {
  images: ProductImage[];
  onImagesChange: (images: ProductImage[]) => void;
  folder?: string;
  maxImages?: number;
}

const MultipleImageUpload: React.FC<MultipleImageUploadProps> = ({
  images,
  onImagesChange,
  folder = 'products',
  maxImages = 10
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [editingImage, setEditingImage] = useState<ProductImage | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [caption, setCaption] = useState<string>('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  // تحميل صورة جديدة أو مجموعة صور
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      console.log('لم يتم اختيار أي ملفات');
      return;
    }

    // التحقق من الحد الأقصى لعدد الصور
    if (images.length + files.length > maxImages) {
      setError(`لا يمكن تحميل أكثر من ${maxImages} صور للمنتج. لديك حاليًا ${images.length} صورة.`);
      return;
    }

    setLoading(true);
    setError(null);

    console.log(`بدء تحميل ${files.length} صور...`);
    console.log('الصور الحالية:', images);

    try {
      // تحويل FileList إلى مصفوفة
      const filesArray = Array.from(files);

      // تحقق من جميع الملفات قبل التحميل
      for (const file of filesArray) {
        // التحقق من نوع الملف
        if (!file.type.startsWith('image/')) {
          setError('يرجى اختيار ملفات صور فقط');
          setLoading(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          return;
        }

        // التحقق من حجم الملف (5MB كحد أقصى)
        if (file.size > 5 * 1024 * 1024) {
          setError('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
          setLoading(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          return;
        }
      }

      // تحميل جميع الصور دفعة واحدة باستخدام الدالة المخصصة لذلك
      console.log(`تحميل ${filesArray.length} صور دفعة واحدة...`);
      
      // استخدام uploadMultipleProductImages بطريقة مباشرة
      const uploadResults = await uploadMultipleProductImages(filesArray, folder);
      console.log('نتائج تحميل الصور:', JSON.stringify(uploadResults));

      if (!uploadResults || uploadResults.length === 0) {
        setError('فشل تحميل الصور، لم يتم استلام أي نتائج');
        setLoading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // إنشاء كائنات صور جديدة من نتائج التحميل، والتأكد من وجود روابط صالحة
      const newImages: ProductImage[] = uploadResults
        .filter(result => result && result.imageUrl) // تصفية النتائج غير الصالحة
        .map((result, index) => ({
          id: `img_${Date.now()}_${Math.random().toString(36).substring(2, 9)}_${index}`,
          url: result.imageUrl, // استخدام imageUrl المحدد في دالة التحميل
          imgbbUrl: result.imgbbUrl || '',
          imagekitUrl: result.imagekitUrl || '',
          isMain: images.length === 0 && index === 0, // الصورة الأولى هي الرئيسية فقط إذا لم تكن هناك صور أخرى
          sortOrder: images.length + index,
          caption: '',
          createdAt: new Date().toISOString()
        }));

      console.log('كائنات الصور الجديدة:', JSON.stringify(newImages));

      if (newImages.length === 0) {
        setError('فشل تحميل الصور، لم يتم إنشاء أي كائنات صور صالحة');
        setLoading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // إضافة الصور الجديدة إلى مصفوفة الصور الحالية
      const updatedImages = [...images, ...newImages];
      console.log('مصفوفة الصور المحدثة بعد إضافة الصور الجديدة:', JSON.stringify(updatedImages));

      // استدعاء وظيفة تغيير الصور مع المصفوفة المحدثة
      onImagesChange(updatedImages);

      console.log('اكتمل تحميل جميع الصور بنجاح');
    } catch (err) {
      console.error('خطأ عام في تحميل الصور:', err);
      setError(`حدث خطأ أثناء تحميل الصور: ${err instanceof Error ? err.message : 'خطأ غير معروف'}`);
    } finally {
      setLoading(false);
      // إعادة تعيين حقل الإدخال
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // حذف صورة
  const handleDeleteImage = (imageId: string) => {
    // إنشاء نسخة جديدة من المصفوفة بدون الصورة المحذوفة
    const updatedImages = images.filter(img => img.id !== imageId);

    // التحقق مما إذا كانت الصورة المحذوفة هي الصورة الرئيسية
    const wasMainImage = images.find(img => img.id === imageId)?.isMain;

    // إذا تم حذف الصورة الرئيسية، جعل الصورة الأولى هي الرئيسية
    if (updatedImages.length > 0 && wasMainImage) {
      // إنشاء نسخة جديدة من المصفوفة مع تعيين الصورة الأولى كصورة رئيسية
      const finalImages = updatedImages.map((img, index) => ({
        ...img,
        isMain: index === 0 // الصورة الأولى فقط هي الرئيسية
      }));

      console.log('تم حذف الصورة الرئيسية، تعيين صورة جديدة كرئيسية:', finalImages);
      onImagesChange(finalImages);
    } else {
      // إذا لم تكن الصورة المحذوفة هي الرئيسية، فقط إرجاع المصفوفة المحدثة
      console.log('تم حذف صورة غير رئيسية:', updatedImages);
      onImagesChange(updatedImages);
    }
  };

  // تعيين صورة كرئيسية
  const handleSetMainImage = (imageId: string) => {
    // إنشاء نسخة جديدة من المصفوفة مع تعيين الصورة المحددة كصورة رئيسية
    const updatedImages = images.map(img => ({
      ...img,
      isMain: img.id === imageId
    }));

    console.log('تم تعيين صورة جديدة كرئيسية:', updatedImages);
    onImagesChange([...updatedImages]);
  };

  // فتح مربع حوار تعديل الصورة
  const handleEditImage = (image: ProductImage) => {
    setEditingImage(image);
    setCaption(image.caption || '');
    setEditDialogOpen(true);
  };

  // حفظ تعديلات الصورة
  const handleSaveImageEdit = () => {
    if (!editingImage) return;

    // إنشاء نسخة جديدة من المصفوفة مع تحديث وصف الصورة المحددة
    const updatedImages = images.map(img =>
      img.id === editingImage.id
        ? { ...img, caption: caption }
        : img
    );

    console.log('تم تحديث وصف الصورة:', updatedImages);
    onImagesChange([...updatedImages]);
    setEditDialogOpen(false);
    setEditingImage(null);
  };

  // الحصول على عدد الصور المتبقية التي يمكن تحميلها
  const getRemainingSlots = () => {
    return maxImages - images.length;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        صور المنتج ({images.length}/{maxImages})
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* صور المنتج */}
        {images.map((image, index) => (
          <Grid item xs={6} sm={4} md={3} key={image.id}>
            <Paper
              elevation={3}
              sx={{
                p: 1,
                position: 'relative',
                border: image.isMain ? '2px solid #1976d2' : 'none',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box
                sx={{
                  height: 150,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mb: 1
                }}
              >
                <img
                  src={image.url}
                  alt={image.caption || `صورة ${index + 1}`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
              </Box>

              {/* وصف الصورة */}
              {image.caption && (
                <Typography variant="caption" noWrap sx={{ textAlign: 'center', mb: 1 }}>
                  {image.caption}
                </Typography>
              )}

              {/* أزرار إدارة الصورة */}
              <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'center', gap: 1 }}>
                {/* زر تعيين كصورة رئيسية */}
                <IconButton
                  size="small"
                  color={image.isMain ? 'primary' : 'default'}
                  onClick={() => handleSetMainImage(image.id)}
                  disabled={image.isMain}
                  title={image.isMain ? 'هذه هي الصورة الرئيسية' : 'تعيين كصورة رئيسية'}
                >
                  {image.isMain ? <StarIcon /> : <StarBorderIcon />}
                </IconButton>

                {/* زر تعديل وصف الصورة */}
                <IconButton
                  size="small"
                  onClick={() => handleEditImage(image)}
                  title="تعديل وصف الصورة"
                >
                  <EditIcon />
                </IconButton>

                {/* زر حذف الصورة */}
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDeleteImage(image.id)}
                  title="حذف الصورة"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Paper>
          </Grid>
        ))}

        {/* زر إضافة صورة جديدة */}
        {images.length < maxImages && (
          <Grid item xs={6} sm={4} md={3}>
            <Paper
              elevation={3}
              sx={{
                p: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
                border: '2px dashed #ccc',
                '&:hover': {
                  borderColor: '#1976d2',
                  backgroundColor: '#f5f5f5'
                }
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleImageUpload}
                accept="image/*"
                multiple // تمكين اختيار ملفات متعددة
              />

              {loading ? (
                <CircularProgress size={40} />
              ) : (
                <>
                  <AddIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="body2" textAlign="center">
                    إضافة صورة جديدة
                    {getRemainingSlots() > 1 && (
                      <Box component="span" sx={{ display: 'block', mt: 1, fontSize: '0.8rem', color: 'text.secondary' }}>
                        يمكنك تحميل {getRemainingSlots()} صور إضافية
                      </Box>
                    )}
                  </Typography>
                </>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* مربع حوار لتعديل وصف الصورة */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>تعديل وصف الصورة</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="وصف الصورة"
            fullWidth
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} color="inherit">
            إلغاء
          </Button>
          <Button onClick={handleSaveImageEdit} color="primary">
            حفظ
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MultipleImageUpload;
